// safeFetch — drop-in wrapper around the standard `fetch` that guarantees
// the request CANNOT hang the UI forever.
//
// Why this exists:
//   The Slots / Casino / Sports pages (and many others) used to load by
//   calling fetch(url) directly. When the player's network blipped or the
//   backend was slow, the promise would never resolve and the page would
//   sit on its skeleton spinner forever — what operators reported as
//   "auto-reload doesn't take, frozen".
//
// Behaviour:
//   • Aborts the request after `timeoutMs` (default 12 000 ms).
//   • Honours an externally-provided AbortSignal too — callers can still
//     cancel on unmount.
//   • Resolves to the same Response you'd get from fetch on success.
//   • Throws on timeout / network error (callers already had a `.catch`
//     branch for that — keeping the surface identical is intentional).

export function safeFetch(url, { timeoutMs = 12000, signal, ...init } = {}) {
  const ctrl = new AbortController();
  const timeoutId = setTimeout(() => {
    try { ctrl.abort(); } catch { /* ignore */ }
  }, timeoutMs);

  // If the caller passed their own AbortSignal, propagate its abort to ours
  // so component-unmount logic still works as expected.
  let onExternalAbort = null;
  if (signal) {
    if (signal.aborted) {
      try { ctrl.abort(); } catch { /* ignore */ }
    } else {
      onExternalAbort = () => { try { ctrl.abort(); } catch { /* ignore */ } };
      signal.addEventListener("abort", onExternalAbort);
    }
  }

  return fetch(url, { ...init, signal: ctrl.signal })
    .finally(() => {
      clearTimeout(timeoutId);
      if (signal && onExternalAbort) {
        try { signal.removeEventListener("abort", onExternalAbort); } catch { /* ignore */ }
      }
    });
}

// safeFetchJSON — convenience helper. Returns parsed JSON or `null` on any
// error / non-2xx status. Never throws — perfect for "best-effort" loaders
// like banners, popups, providers list where a failure should NOT crash
// the UI but simply render the empty/cached state.
export async function safeFetchJSON(url, opts = {}) {
  try {
    const r = await safeFetch(url, opts);
    if (!r || !r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}
